import Component from '@glimmer/component';
import { inject as service } from '@ember/service';

import type EnginesRouterService from 'ember-engines-router-service/services/router';

interface PaginationSignature {
  Element: HTMLDivElement;
  Args: {
    margin?: string;
    pagination?: {
      nextPageToken?: string;
      previousPageToken?: string;
    };
  };
}

/**
 *
 * `Pagination` renders a Toolbar component with pagination controls in it.
 *
 *
 * ```
 * <Pagination @pagination={{@model.pagination}} />
 * ```
 *
 * @class Pagination
 *
 */

/**
 * @typedef {Object} Pagination
 * @property {string} nextPageToken the next pagination token
 * @property {string} previousPageToken the previous pagination token
 */

/**
 * The pagination object from the api.
 * @argument pagination
 * @type {Pagination}
 * @required
 */

/**
 * Configurable margin value
 * @argument margin
 * @type {String} "sm 0 0 0" by default
 * @optional
 */

export default class PaginationComponent extends Component<PaginationSignature> {
  @service declare readonly router: EnginesRouterService;

  get currentRouteName() {
    return this.router.currentRouteName;
  }

  get showPagination() {
    return (
      this.args.pagination?.previousPageToken ||
      this.args.pagination?.nextPageToken
    );
  }

  get paginationQueryFunction() {
    return (page: 'prev' | 'next') => {
      return {
        prev:
          page === 'prev' ? this.args.pagination?.previousPageToken : undefined,
        next: page === 'next' ? this.args.pagination?.nextPageToken : undefined,
      };
    };
  }

  get paginationIsDisabledPrev() {
    return !this.args.pagination?.previousPageToken;
  }

  get paginationIsDisabledNext() {
    return !this.args.pagination?.nextPageToken;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    Pagination: typeof PaginationComponent;
    pagination: typeof PaginationComponent;
  }
}
